import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomCalendarGestion from "./components/CustomCalendarGestion";
import callApi from "../../../../services/callApi";
import logoImage from "../../../../assets/images/logo.png";

// Componente HourSelector inline (copiado de Calendario.jsx)
const HourSelector = ({ availableHours, onSelectHour }) => {
  const [selectedHour, setSelectedHour] = useState(null);

  const handleHourClick = (hour) => {
    setSelectedHour(hour);
    onSelectHour(hour);
  };

  // Separar horas en mañana y tarde
  const morningHours = availableHours.filter(hour => {
    const hourNum = parseInt(hour.split(':')[0]);
    return hourNum < 12;
  });

  const afternoonHours = availableHours.filter(hour => {
    const hourNum = parseInt(hour.split(':')[0]);
    return hourNum >= 12;
  });

  // Calcular ancho uniforme basado en la fila con más elementos
  const calculateUniformWidth = () => {
    const maxElementsPerRow = isMobile ? 4 : 5;
    const morningNeedsMultipleRows = morningHours.length >= maxElementsPerRow;
    const afternoonNeedsMultipleRows = afternoonHours.length >= maxElementsPerRow;
    
    if (!morningNeedsMultipleRows && !afternoonNeedsMultipleRows) {
      return "90px";
    }
    
    // Calcular ancho disponible del contenedor
    const containerWidth = isMobile ? 
      (window.innerWidth * 0.94 - 66) : // Mobile: 94% menos padding
      (480); // Desktop: ancho real disponible después de todos los paddings
    
    const gap = 4; // 0.5 * 8px
    const elementsInFullRow = maxElementsPerRow;
    
    // Calcular ancho por elemento para que la fila completa ocupe todo el ancho
    const uniformWidth = (containerWidth - (gap * (elementsInFullRow - 1))) / elementsInFullRow;
    
    return `${Math.floor(uniformWidth)}px`;
  };

  const uniformWidth = calculateUniformWidth();

  const renderHourButton = (hour) => (
    <Button
      key={hour}
      onClick={() => handleHourClick(hour)}
      disableRipple
      disableElevation
      disableFocusRipple
      sx={{
        minWidth: uniformWidth,
        width: uniformWidth,
        height: "40px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
        color: selectedHour === hour ? "white" : "#191C1F",
        border: selectedHour === hour ? "none" : "1px solid #d0d0d0",
        fontFamily: selectedHour === hour ? "Quicksand-Bold" : "Quicksand-Medium",
        fontSize: { xs: "14px", md: "16px" },
        borderRadius: 2,
        boxShadow: "none !important",
        transition: "none !important",
        textTransform: "none",
        "&:hover": { 
            backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
            boxShadow: "none !important",
        },
        "&:active": {
          backgroundColor: selectedHour === hour ? "#3B82F6" : "#3B82F6",
          boxShadow: "none !important",
        },
        "&:focus": {
          backgroundColor: selectedHour === hour ? "#3B82F6" : "#3B82F6",
          boxShadow: "none !important",
        },
        "& .MuiTouchRipple-root": {
          display: "none",
        },
      }}
    >
      {hour}
    </Button>
  );

  return (
    <Box>
      {morningHours.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "15px", md: "16px" },
              color: "#626262",
              mb: 1,
            }}
          >
            Horarios AM:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {morningHours.map(hour => renderHourButton(hour))}
          </Box>
        </Box>
      )}
      
      {afternoonHours.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "15px", md: "16px" },
              color: "#626262",
              mb: 1,
            }}
          >
            Horarios PM:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {afternoonHours.map(hour => renderHourButton(hour))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ==================== COMPONENTES EXTRAÍDOS ====================

// Componente para la sección de Confirmar
const ConfirmarSeccion = ({ datosApi, confirmando, confirmadoExito, errorConfirmacion, mostrarIcono, onConfirmar }) => {
  const renderizarContenidoBoton = () => {
    if (confirmando) {
      return <CircularProgress size={24} sx={{ color: "white" }} />;
    }
    if (mostrarIcono && !errorConfirmacion) {
      return <CheckCircleIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (mostrarIcono && errorConfirmacion) {
      return <CancelIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (confirmadoExito) {
      return "CONFIRMADO";
    }
    if (errorConfirmacion) {
      return "ERROR";
    }
    return "CONFIRMAR";
  };

  const obtenerColorBoton = () => {
    if (confirmadoExito) return "#22C55E";
    if (errorConfirmacion) return "#FF6B35";
    if (datosApi?.accion_permitida) return "#3B82F6";
    return "#d0d0d0";
  };

  const obtenerColorHover = () => {
    if (confirmadoExito) return "#22C55E";
    if (errorConfirmacion) return "#FF6B35";
    if (datosApi?.accion_permitida) return "#2563EB";
    return "#d0d0d0";
  };

  return (
    <>
      <Typography
        sx={{
          fontFamily: "Quicksand-Regular",
          fontSize: { xs: "14px", md: "16px" },
          color: "#626262",
          lineHeight: 1.6,
          textAlign: "center",
          mb: 3,
        }}
      >
        {!confirmadoExito 
          ? "Tu profesional será notificado de que asistirás a tu sesión."
          : "Hemos confirmado tu asistencia a esta sesión."
        }
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          onClick={onConfirmar}
          disabled={!datosApi.accion_permitida || confirmando || confirmadoExito || errorConfirmacion}
          disableRipple
          disableElevation
          disableFocusRipple
          sx={{
            width: { xs: "190px", md: "210px" },
            backgroundColor: obtenerColorBoton(),
            color: "#FFFFFF",
            padding: "14px 40px",
            borderRadius: 2,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "14px", md: "16px" },
            textTransform: "none",
            cursor: datosApi.accion_permitida && !confirmando && !confirmadoExito && !errorConfirmacion ? "pointer" : "not-allowed",
            boxShadow: "none !important",
            transition: "none !important",
            "&:hover": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:active": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:focus": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:disabled": {
              backgroundColor: obtenerColorBoton(),
              color: "#FFFFFF",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
          }}
        >
          {renderizarContenidoBoton()}
        </Button>
      </Box>
    </>
  );
};

// Componente para la sección de Reagendar
const ReagendarSeccion = ({ 
  datosApi, 
  reagendando, 
  reagendadoExito, 
  errorReagendacion, 
  mostrarIconoReagendar,
  selectedDate,
  selectedTime,
  horasDisponibles,
  onReagendar,
  onSeleccionFecha,
  onSeleccionHora,
  formatearFechaLimite
}) => {
  const renderizarContenidoBoton = () => {
    if (reagendando) {
      return <CircularProgress size={24} sx={{ color: "white" }} />;
    }
    if (mostrarIconoReagendar && !errorReagendacion) {
      return <CheckCircleIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (mostrarIconoReagendar && errorReagendacion) {
      return <CancelIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (reagendadoExito) {
      return "REAGENDADO";
    }
    if (errorReagendacion) {
      return "ERROR";
    }
    return "REAGENDAR";
  };

  const obtenerColorBoton = () => {
    if (reagendadoExito) return "#22C55E";
    if (errorReagendacion) return "#FF6B35";
    if (datosApi?.accion_permitida && selectedDate && selectedTime) return "#3B82F6";
    return "#d0d0d0";
  };

  const obtenerColorHover = () => {
    if (reagendadoExito) return "#22C55E";
    if (errorReagendacion) return "#FF6B35";
    if (datosApi?.accion_permitida && selectedDate && selectedTime) return "#2563EB";
    return "#d0d0d0";
  };

  return (
    <>
      {!reagendadoExito ? (
        <>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#626262",
              lineHeight: 1.6,
              mb: 3,
              "& strong": {
                fontFamily: "Quicksand-Bold",
              },
            }}
          >
            Tienes hasta el <strong>{formatearFechaLimite(datosApi.fecha_limite_reagendar)}</strong> para cambiar el día y hora de tu sesión. Puedes cambiar tu sesión hasta <strong>{datosApi.reagendaciones_restantes}</strong> {datosApi.reagendaciones_restantes === 1 ? "vez" : "veces"}.
          </Typography>

          <Box
            sx={{
              borderRadius: 3,
              backgroundColor: "white",
              borderColor: "#d0d0d0",
              borderWidth: 1,
              borderStyle: "solid",
              overflow: "hidden",
              p: 2,
            }}
          >
            <CustomCalendarGestion
              disponibilidad={datosApi.disponibilidad}
              onDateChange={onSeleccionFecha}
            />
            <Box
              sx={{
                mt: 2,
                pb: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: "10px", md: "16px" },
              }}
            >
              <Box
                sx={{
                  display: "inline-block",
                  width: "45px",
                  height: "15px",
                  borderRadius: 1,
                  backgroundColor: "#EEF5FF",
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Quicksand-Bold",
                  fontSize: { xs: "12px", md: "14px" },
                }}
              >
                Disponible
              </Typography>
              <Box
                sx={{
                  display: "inline-block",
                  width: "45px",
                  height: "15px",
                  borderRadius: 1,
                  backgroundColor: "#3B82F6",
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Quicksand-Bold",
                  fontSize: { xs: "12px", md: "14px" },
                }}
              >
                Seleccionado
              </Typography>
            </Box>
          </Box>

          {selectedDate && horasDisponibles.length > 0 && (
            <>
              <Typography
                sx={{
                  fontFamily: "Quicksand-Bold",
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#007FFF",
                  mt: 2,
                  mb: 0,
                }}
              >
                {new Date(selectedDate + "T00:00:00").toLocaleDateString("es-ES", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#626262",
                  mt: 1,
                  mb: 0,
                }}
              >
                Horarios disponibles:
              </Typography>

              <Box sx={{ mt: 1 }}>
                <HourSelector
                  availableHours={horasDisponibles}
                  onSelectHour={onSeleccionHora}
                />
              </Box>
            </>
          )}
        </>
      ) : (
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#626262",
            lineHeight: 1.6,
            textAlign: "center",
            mb: 3,
            "& strong": {
              fontFamily: "Quicksand-Bold",
            },
          }}
        >
          Hemos reagendado tu sesión al <strong>{new Date(selectedDate + "T00:00:00").toLocaleDateString("es-ES", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })} a las {selectedTime}</strong>
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          onClick={onReagendar}
          disabled={!datosApi.accion_permitida || !selectedDate || !selectedTime || reagendando || reagendadoExito || errorReagendacion}
          disableRipple
          disableElevation
          disableFocusRipple
          sx={{
            width: { xs: "190px", md: "210px" },
            backgroundColor: obtenerColorBoton(),
            color: "#FFFFFF",
            padding: "14px 40px",
            borderRadius: 2,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "14px", md: "16px" },
            textTransform: "none",
            cursor: datosApi.accion_permitida && selectedDate && selectedTime && !reagendando && !reagendadoExito && !errorReagendacion ? "pointer" : "not-allowed",
            boxShadow: "none !important",
            transition: "none !important",
            "&:hover": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:active": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:focus": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:disabled": {
              backgroundColor: obtenerColorBoton(),
              color: "#FFFFFF",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
          }}
        >
          {renderizarContenidoBoton()}
        </Button>
      </Box>
    </>
  );
};

// Componente para la sección de Cancelar/Suspender
const CancelarSeccion = ({ 
  datosApi, 
  suspendiendo, 
  suspendidoExito, 
  errorSuspension, 
  mostrarIconoSuspender,
  onCancelar,
  formatearFechaLimite
}) => {
  const renderizarContenidoBoton = () => {
    if (suspendiendo) {
      return <CircularProgress size={24} sx={{ color: "white" }} />;
    }
    if (mostrarIconoSuspender && !errorSuspension) {
      return <CheckCircleIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (mostrarIconoSuspender && errorSuspension) {
      return <CancelIcon sx={{ fontSize: 32, color: "white" }} />;
    }
    if (suspendidoExito) {
      return "SUSPENDIDA";
    }
    if (errorSuspension) {
      return "ERROR";
    }
    return "SUSPENDER";
  };

  const obtenerColorBoton = () => {
    if (suspendidoExito) return "#22C55E";
    if (errorSuspension) return "#FF6B35";
    if (datosApi?.accion_permitida) return "#3B82F6";
    return "#d0d0d0";
  };

  const obtenerColorHover = () => {
    if (suspendidoExito) return "#22C55E";
    if (errorSuspension) return "#FF6B35";
    if (datosApi?.accion_permitida) return "#2563EB";
    return "#d0d0d0";
  };

  return (
    <>
      {!suspendidoExito ? (
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#626262",
            textAlign: { xs: "center", md: "left" },
            lineHeight: 1.6,
            mb: 3,
            "& strong": {
              fontFamily: "Quicksand-Bold",
            },
          }}
        >
          Tienes hasta el <strong>{formatearFechaLimite(datosApi.fecha_limite_cancelar)}</strong> para suspender tu sesión.
        </Typography>
      ) : (
        <Typography
          sx={{
            fontFamily: "Quicksand-Regular",
            fontSize: { xs: "14px", md: "16px" },
            color: "#626262",
            lineHeight: 1.6,
            textAlign: "center",
            mb: 3,
            "& strong": {
              fontFamily: "Quicksand-Bold",
            },
          }}
        >
          Has <strong>suspendido</strong> tu sesión del <strong>{new Date(datosApi.sesion_fecha + "T00:00:00").toLocaleDateString("es-ES", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })} a las {datosApi.sesion_hora}</strong>.
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          onClick={onCancelar}
          disabled={!datosApi.accion_permitida || suspendiendo || suspendidoExito || errorSuspension}
          disableRipple
          disableElevation
          disableFocusRipple
          sx={{
            width: { xs: "190px", md: "210px" },
            backgroundColor: obtenerColorBoton(),
            color: "#FFFFFF",
            padding: "14px 40px",
            borderRadius: 2,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "14px", md: "16px" },
            textTransform: "none",
            cursor: datosApi.accion_permitida && !suspendiendo && !suspendidoExito && !errorSuspension ? "pointer" : "not-allowed",
            boxShadow: "none !important",
            transition: "none !important",
            "&:hover": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:active": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:focus": {
              backgroundColor: obtenerColorHover(),
              boxShadow: "none !important",
            },
            "&:disabled": {
              backgroundColor: obtenerColorBoton(),
              color: "#FFFFFF",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
          }}
        >
          {renderizarContenidoBoton()}
        </Button>
      </Box>
    </>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

function GestionSesion() {
  const { sesion_link } = useParams();
  const location = useLocation();
  
  // Estados para datos de API
  const [datosApi, setDatosApi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para selector (reagendarocancelar)
  const [datosApiReagendar, setDatosApiReagendar] = useState(null);
  const [datosApiCancelar, setDatosApiCancelar] = useState(null);
  const [mostrarSelector, setMostrarSelector] = useState(true);
  const [accionSeleccionada, setAccionSeleccionada] = useState(null);

  // Estados para calendario (reagendar)
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [horasDisponibles, setHorasDisponibles] = useState([]);

  // Estados para confirmación
  const [confirmando, setConfirmando] = useState(false);
  const [confirmadoExito, setConfirmadoExito] = useState(false);
  const [errorConfirmacion, setErrorConfirmacion] = useState(false);
  const [mostrarIcono, setMostrarIcono] = useState(false);

  // Estados para reagendamiento
  const [reagendando, setReagendando] = useState(false);
  const [reagendadoExito, setReagendadoExito] = useState(false);
  const [errorReagendacion, setErrorReagendacion] = useState(false);
  const [mostrarIconoReagendar, setMostrarIconoReagendar] = useState(false);

  // Estados para suspensión
  const [suspendiendo, setSuspendiendo] = useState(false);
  const [suspendidoExito, setSuspendidoExito] = useState(false);
  const [errorSuspension, setErrorSuspension] = useState(false);
  const [mostrarIconoSuspender, setMostrarIconoSuspender] = useState(false);

  // Detectar acción desde URL
  const accionTipo = location.pathname.includes("/confirmar/")
    ? "confirmar"
    : location.pathname.includes("/reagendar/")
    ? "reagendar"
    : location.pathname.includes("/cancelar/")
    ? "cancelar"
    : location.pathname.includes("/reagendarocancelar/")
    ? "reagendarocancelar"
    : location.pathname.includes("/cekib/")
    ? "cekib"
    : null;

  // Fetch inicial de datos
  useEffect(() => {
    const fetchDatos = async () => {
      try {
        setLoading(true);
        setError(null);

        if (accionTipo === "reagendarocancelar") {
          // Fetch SECUENCIAL para selector
          console.log("Fetching datos para reagendar...");
          const dataReagendar = await callApi(`/agenda/infoConfirmarReagendarCancelar?sesion_link=${sesion_link}&accion=reagendar`);
          
          // Validar si es un error
          if (dataReagendar && dataReagendar.response && dataReagendar.response.status) {
            throw new Error("Error al cargar datos de reagendar");
          }
          
          setDatosApiReagendar(dataReagendar);
          console.log("Datos de reagendar cargados exitosamente");

          // Segundo fetch después del primero
          console.log("Fetching datos para cancelar...");
          const dataCancelar = await callApi(`/agenda/infoConfirmarReagendarCancelar?sesion_link=${sesion_link}&accion=cancelar`);
          
          // Validar si es un error
          if (dataCancelar && dataCancelar.response && dataCancelar.response.status) {
            throw new Error("Error al cargar datos de cancelar");
          }
          
          setDatosApiCancelar(dataCancelar);
          console.log("Datos de cancelar cargados exitosamente");

        } else if (accionTipo === "cekib") {
          // Fetch para acción CEKIB - API diferente sin parámetro "accion"
          const url = `/agenda/infoConfirmarReagendarCancelarCekib?sesion_link=${sesion_link}`;
          const data = await callApi(url);
          
          // Validar si es un error
          if (data && data.response && data.response.status) {
            throw new Error("Error al cargar datos CEKIB");
          }
          
          setDatosApi(data);
        } else {
          // Fetch único para acciones directas (confirmar/reagendar/cancelar)
          const url = `/agenda/infoConfirmarReagendarCancelar?sesion_link=${sesion_link}&accion=${accionTipo}`;
          const data = await callApi(url);
          
          // Validar si es un error
          if (data && data.response && data.response.status) {
            throw new Error("Error al cargar datos");
          }
          
          setDatosApi(data);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error en fetch:", err);
        setError("Error al cargar los datos de la sesión");
        setDatosApi(null);
        setDatosApiReagendar(null);
        setDatosApiCancelar(null);
        setLoading(false);
      }
    };

    if (sesion_link && accionTipo) {
      fetchDatos();
    }
  }, [sesion_link, accionTipo]);

  // Función para formatear fecha límite
  const formatearFechaLimite = (fechaString) => {
    if (!fechaString) return "";
    
    const [fecha, hora] = fechaString.split(" ");
    const [year, month, day] = fecha.split("-");
    const dateObj = new Date(year, month - 1, day);
    
    const diasSemana = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    
    const diaSemana = diasSemana[dateObj.getDay()];
    const mes = meses[dateObj.getMonth()];
    
    return `${diaSemana} ${day} de ${mes} del ${year} a las ${hora}`;
  };

  // Función para transformar modalidad
  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  // Función para generar mensaje de rechazo
  const generarMensajeRechazo = (datosReferencia, tipoAccion) => {
    if (!datosReferencia) return "";
    
    const { razon_rechazo, fecha_limite_reagendar, fecha_limite_cancelar, contador_reagendaciones_paciente } = datosReferencia;
    
    switch (razon_rechazo) {
      case "sesion_no_agendada":
        if (datosReferencia.sesion_estado_ejecucion === "completada") {
          return "Esta sesión ya fue completada y no puede ser " + (tipoAccion === "confirmar" ? "confirmada" : tipoAccion === "reagendar" ? "reagendada" : "suspendida") + ".";
        }
        return "Esta sesión está " + datosReferencia.sesion_estado_ejecucion + " y no puede ser " + (tipoAccion === "confirmar" ? "confirmada" : tipoAccion === "reagendar" ? "reagendada" : "suspendida") + ".";
      
      case "permiso_deshabilitado":
        return "El profesional no se encuentra aceptando " + 
               (tipoAccion === "confirmar" ? "confirmaciones" : tipoAccion === "reagendar" ? "reagendaciones" : "suspensiones") + 
               " en sus sesiones en este momento.";
      
      case "tiempo_insuficiente":
        const fechaLimite = tipoAccion === "reagendar" ? fecha_limite_reagendar : fecha_limite_cancelar;
        const fechaFormateada = formatearFechaLimite(fechaLimite);
        return (
          <>
            <strong>Ya no puedes {tipoAccion === "confirmar" ? "confirmar" : tipoAccion === "reagendar" ? "reagendar" : "suspender"} esta sesión</strong>. Esta sesión podía ser {tipoAccion === "confirmar" ? "confirmada" : tipoAccion === "reagendar" ? "reagendada" : "suspendida"} hasta el <strong>{fechaFormateada}</strong>.
          </>
        );
      
      case "limite_reagendaciones_excedido":
        const veces = contador_reagendaciones_paciente === 1 ? "vez" : "veces";
        return (
          <>
            Esta sesión ya fue reagendada <strong>{contador_reagendaciones_paciente} {veces}</strong>, por lo que no puede volver a ser reagendada.
          </>
        );
      
      default:
        return "No se puede realizar esta acción en este momento.";
    }
  };

  // Handler para selección de fecha en calendario
  const manejarSeleccionFecha = (fecha, horas) => {
    setSelectedDate(fecha);
    setHorasDisponibles(horas || []);
    setSelectedTime(null);
  };

  // Handler para selección de hora
  const manejarSeleccionHora = (hora) => {
    setSelectedTime(hora);
  };

  // Handler para selección de acción en selector
  const handleSeleccionAccion = (tipo) => {
    setAccionSeleccionada(tipo);
    setMostrarSelector(false);
    
    // Copiar datos correspondientes a datosApi
    if (tipo === "reagendar") {
      setDatosApi(datosApiReagendar);
    } else if (tipo === "cancelar") {
      setDatosApi(datosApiCancelar);
    }
  };

  // Handlers para botones de acción
  const handleConfirmar = async () => {
    setConfirmando(true);

    try {
      const response = await callApi('/agenda/accionConfirmarSesion', 'post', {
        sesion_link: sesion_link
      });

      // Verificar si la respuesta es un objeto de error de axios
      if (response && response.response && response.response.status) {
        throw new Error("Error en la llamada API");
      }

      if (response && response.confirmada === true) {
        // Éxito
        setConfirmando(false);
        setMostrarIcono(true);

        setTimeout(() => {
          setMostrarIcono(false);
          setConfirmadoExito(true);
        }, 300);
      } else {
        throw new Error("Confirmación fallida");
      }
    } catch (err) {
      setConfirmando(false);
      setMostrarIcono(true);
      setErrorConfirmacion(true);

      setTimeout(() => {
        setMostrarIcono(false);
      }, 300);

      setTimeout(() => {
        setErrorConfirmacion(false);
      }, 2000);
    }
  };

  const handleReagendar = async () => {
    setReagendando(true);

    try {
      // Convertir fecha de YYYY-MM-DD a dd/mm/yyyy
      const [year, month, day] = selectedDate.split('-');
      const fechaNuevaFormato = `${day}/${month}/${year}`;

      const response = await callApi('/agenda/accionReagendarSesion', 'post', {
        sesion_link: sesion_link,
        fecha_nueva: fechaNuevaFormato,
        hora_nueva: selectedTime
      });

      if (response && response.response && response.response.status) {
        throw new Error("Error en la llamada API");
      }

      if (response && response.reagendada === true) {
        setReagendando(false);
        setMostrarIconoReagendar(true);

        setTimeout(() => {
          setMostrarIconoReagendar(false);
          setReagendadoExito(true);
        }, 300);
      } else {
        throw new Error("Reagendamiento fallido");
      }
    } catch (err) {
      setReagendando(false);
      setMostrarIconoReagendar(true);
      setErrorReagendacion(true);

      setTimeout(() => {
        setMostrarIconoReagendar(false);
      }, 300);

      setTimeout(() => {
        setErrorReagendacion(false);
      }, 2000);
    }
  };

  const handleCancelar = async () => {
    setSuspendiendo(true);

    try {
      const response = await callApi('/agenda/accionSuspenderSesion', 'post', {
        sesion_link: sesion_link
      });

      if (response && response.response && response.response.status) {
        throw new Error("Error en la llamada API");
      }

      if (response && response.suspendida === true) {
        setSuspendiendo(false);
        setMostrarIconoSuspender(true);

        setTimeout(() => {
          setMostrarIconoSuspender(false);
          setSuspendidoExito(true);
        }, 300);
      } else {
        throw new Error("Suspensión fallida");
      }
    } catch (err) {
      setSuspendiendo(false);
      setMostrarIconoSuspender(true);
      setErrorSuspension(true);

      setTimeout(() => {
        setMostrarIconoSuspender(false);
      }, 300);

      setTimeout(() => {
        setErrorSuspension(false);
      }, 2000);
    }
  };

  // Handler para abrir WhatsApp CEKIB en nueva pestaña
  const handleAbrirWhatsappCekib = () => {
    if (datosApi && datosApi.whatsapp_cekib) {
      window.open(datosApi.whatsapp_cekib, '_blank');
    }
  };

  // Renderizado de loading
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Renderizado de error
  if (error || (!datosApi && accionTipo !== "reagendarocancelar") || (accionTipo === "reagendarocancelar" && (!datosApiReagendar || !datosApiCancelar))) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography 
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            fontSize: { xs: "14px", md: "16px" }, 
            color: "#626262", 
            mb: 1 
          }}
        >
          No pudimos cargar los datos de tu sesión.
        </Typography>
        <Typography 
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            fontSize: { xs: "14px", md: "16px" }, 
            color: "#626262" 
          }}
        >
          Por favor intenta nuevamente o contacta a tu profesional para más información.
        </Typography>
      </Box>
    );
  }

  // Renderizado especial para CEKIB (solo botón de WhatsApp)
  if (accionTipo === "cekib" && datosApi) {
    return (
      <>
        {/* Logo Clinifica */}
        <img
          src={logoImage}
          alt="Clinifica Logo"
          style={{
            width: isMobile ? "150px" : "200px",
            height: "auto",
            paddingTop: "15px",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            padding: 15,
          }}
        />

        {/* Caja superior con información de sesión */}
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            mb: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fbfafe",
              borderRadius: 2,
              p: isMobile ? 2 : 4,
              boxShadow: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#626262",
                mb: 0,
              }}
            >
              Estás gestionando tu sesión con:
            </Typography>

            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0,
              }}
            >
              {datosApi.profesional_nombres} {datosApi.profesional_apellidos}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                mt: -0.5,
                mb: 0,
              }}
            >
              {datosApi.profesional_profesion}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#626262",
                mt: 2,
                mb: 0,
              }}
            >
              El servicio de:
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mt: -0.5,
                mb: 0,
              }}
            >
              {datosApi.sesion_nombre_servicio}
            </Typography>

            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f0f4ff",
                color: "#3B82F6",
                fontFamily: "Quicksand-Medium",
                fontSize: { xs: "13px", md: "15px" },
                borderRadius: 2,
                paddingLeft: 0.75,
                paddingRight: 0.75,
                paddingTop: 0.25,
                paddingBottom: 0.25,
                marginLeft: -0.75,
                mt: -0.75,
                textAlign: "left",
              }}
            >
              {transformModalidad(datosApi.sesion_tipo)}
            </Box>

            <Typography
              sx={{
                mt: 0,
                color: "#007FFF",
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {new Date(datosApi.sesion_fecha + "T00:00:00").toLocaleDateString("es-ES", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })} - {datosApi.sesion_hora}
            </Typography>
          </Box>
        </Box>

        {/* Título de sección */}
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "20px", md: "24px" },
            textAlign: "center",
            color: "#191C1F",
            mt: 3,
            mb: 1,
          }}
        >
          Contacta al centro CEKIB
        </Typography>

        {/* Caja inferior con botón de WhatsApp */}
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            mb: 5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: isMobile ? 2 : 4,
              boxShadow: 1,
            }}
          >
            {/* Wrapper con padding lateral para desktop */}
            <Box sx={{ px: { xs: 0, md: "15%" } }}>
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#626262",
                  lineHeight: 1.6,
                  textAlign: "center",
                  mb: 3,
                }}
              >
                Para gestionar tu sesión (cambiar día, hora o suspender), contacta directamente al centro CEKIB a través de WhatsApp. Ellos te ayudarán con tu solicitud.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  onClick={handleAbrirWhatsappCekib}
                  disableRipple
                  disableElevation
                  disableFocusRipple
                  sx={{
                    width: { xs: "280px", md: "320px" },
                    backgroundColor: "#3B82F6",
                    color: "#FFFFFF",
                    padding: "14px 40px",
                    borderRadius: 2,
                    fontFamily: "Quicksand-Bold",
                    fontSize: { xs: "14px", md: "16px" },
                    textTransform: "none",
                    cursor: "pointer",
                    boxShadow: "none !important",
                    transition: "none !important",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                      boxShadow: "none !important",
                    },
                    "&:active": {
                      backgroundColor: "#2563EB",
                      boxShadow: "none !important",
                    },
                    "&:focus": {
                      backgroundColor: "#2563EB",
                      boxShadow: "none !important",
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none",
                    },
                  }}
                >
                  CONTACTAR POR WHATSAPP
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  // Renderizado del selector (reagendarocancelar)
  if (accionTipo === "reagendarocancelar" && mostrarSelector) {
    const datosReferencia = datosApiReagendar || datosApiCancelar;
    
    return (
      <>
        {/* Logo Clinifica */}
        <img
          src={logoImage}
          alt="Clinifica Logo"
          style={{
            width: isMobile ? "150px" : "200px",
            height: "auto",
            paddingTop: "15px",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
            padding: 15,
          }}
        />

        {/* Caja superior con información de sesión */}
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            mb: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fbfafe",
              borderRadius: 2,
              p: isMobile ? 2 : 4,
              boxShadow: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#626262",
                mb: 0,
              }}
            >
              Estás gestionando tu sesión con:
            </Typography>

            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0,
              }}
            >
              {datosReferencia.profesional_nombres} {datosReferencia.profesional_apellidos}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                mt: -0.5,
                mb: 0,
              }}
            >
              {datosReferencia.profesional_profesion}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                color: "#626262",
                mt: 2,
                mb: 0,
              }}
            >
              El servicio de:
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mt: -0.5,
                mb: 0,
              }}
            >
              {datosReferencia.sesion_nombre_servicio}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", mt: -0.5, mb: 0, flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { xs: "14px", md: "16px" },
                  mr: 0.5,
                }}
              >
                ${datosReferencia.valor_cobro.toLocaleString("es-CL")}
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Quicksand-Bold",
                  fontSize: { xs: "14px", md: "16px" },
                  color: "#007FFF",
                }}
              >
                {datosReferencia.estado_cobro === "pagado" ? "pagado" : "pendiente de pago"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f0f4ff",
                color: "#3B82F6",
                fontFamily: "Quicksand-Medium",
                fontSize: { xs: "13px", md: "15px" },
                borderRadius: 2,
                paddingLeft: 0.75,
                paddingRight: 0.75,
                paddingTop: 0.25,
                paddingBottom: 0.25,
                marginLeft: -0.75,
                mt: -0.75,
                textAlign: "left",
              }}
            >
              {transformModalidad(datosReferencia.sesion_tipo)}
            </Box>

            <Typography
              sx={{
                mt: 0,
                color: "#007FFF",
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "16px" },
              }}
            >
              {new Date(datosReferencia.sesion_fecha + "T00:00:00").toLocaleDateString("es-ES", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })} - {datosReferencia.sesion_hora}
            </Typography>
          </Box>
        </Box>

        {/* Título de sección */}
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "20px", md: "24px" },
            textAlign: "center",
            color: "#191C1F",
            mt: 3,
            mb: 1,
          }}
        >
          Gestiona tu sesión
        </Typography>

        {/* Caja #1 - REAGENDAR */}
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            mb: 2, // Cambio: mb de 5 a 2 para separar de la siguiente caja
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: isMobile ? 2 : 4,
              boxShadow: 1,
            }}
          >
            {/* Wrapper con padding lateral para desktop */}
            <Box sx={{ px: { xs: 0, md: "15%" } }}>
              
              {/* SECCIÓN REAGENDAR */}
              <Box> {/* Cambio: removido el mb: 3 */}
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Bold",
                    fontSize: { xs: "20px", md: "24px" },
                    color: "#191C1F",
                    mb: 1,
                  }}
                >
                  Reagendar sesión
                </Typography>

                {/* Mensaje de alerta si NO está permitido */}
                {!datosApiReagendar.accion_permitida && (
                  <Box
                    sx={{
                      backgroundColor: "#FEF3E2",
                      borderRadius: 2,
                      p: isMobile ? 2 : 3,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Quicksand-Regular",
                        fontSize: { xs: "14px", md: "16px" },
                        color: "#5D4E37",
                        lineHeight: 1.5,
                        "& strong": {
                          fontFamily: "Quicksand-Bold",
                          color: "#3E2F1F",
                        },
                      }}
                    >
                      {generarMensajeRechazo(datosApiReagendar, "reagendar")}
                    </Typography>
                  </Box>
                )}

                {/* Información si está permitido */}
                {datosApiReagendar.accion_permitida && (
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      color: "#626262",
                      lineHeight: 1.6,
                      mb: 2,
                      "& strong": {
                        fontFamily: "Quicksand-Bold",
                      },
                    }}
                  >
                    Tienes hasta el <strong>{formatearFechaLimite(datosApiReagendar.fecha_limite_reagendar)}</strong> para cambiar el día y hora de tu sesión. Puedes cambiar tu sesión hasta <strong>{datosApiReagendar.reagendaciones_restantes}</strong> {datosApiReagendar.reagendaciones_restantes === 1 ? "vez" : "veces"}.
                  </Typography>
                )}

                {/* Botón CONTINUAR REAGENDAR */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    onClick={() => handleSeleccionAccion("reagendar")}
                    disabled={!datosApiReagendar.accion_permitida}
                    disableRipple
                    disableElevation
                    disableFocusRipple
                    sx={{
                      width: { xs: "380px", md: "420px" },
                      backgroundColor: datosApiReagendar.accion_permitida ? "#3B82F6" : "#d0d0d0",
                      color: "#FFFFFF",
                      padding: "14px 40px",
                      borderRadius: 2,
                      fontFamily: "Quicksand-Bold",
                      fontSize: { xs: "14px", md: "16px" },
                      textTransform: "none",
                      cursor: datosApiReagendar.accion_permitida ? "pointer" : "not-allowed",
                      boxShadow: "none !important",
                      transition: "none !important",
                      "&:hover": {
                        backgroundColor: datosApiReagendar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:active": {
                        backgroundColor: datosApiReagendar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:focus": {
                        backgroundColor: datosApiReagendar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:disabled": {
                        backgroundColor: "#d0d0d0",
                        color: "#FFFFFF",
                      },
                      "& .MuiTouchRipple-root": {
                        display: "none",
                      },
                    }}
                  >
                    CONTINUAR REAGENDAR
                  </Button>
                </Box>
              </Box>

            </Box>
          </Box>
        </Box>

        {/* Caja #2 - SUSPENDER - NUEVA CAJA INDEPENDIENTE */}
        <Box
          sx={{
            maxWidth: isMobile ? "94%" : "800px",
            width: isMobile ? "94%" : "90%",
            mx: "auto",
            mb: 5,
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: isMobile ? 2 : 4,
              boxShadow: 1,
            }}
          >
            {/* Wrapper con padding lateral para desktop */}
            <Box sx={{ px: { xs: 0, md: "15%" } }}>

              {/* SECCIÓN SUSPENDER */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Bold",
                    fontSize: { xs: "20px", md: "24px" },
                    color: "#191C1F",
                    mb: 1,
                  }}
                >
                  Suspender sesión
                </Typography>

                {/* Mensaje de alerta si NO está permitido */}
                {!datosApiCancelar.accion_permitida && (
                  <Box
                    sx={{
                      backgroundColor: "#FEF3E2",
                      borderRadius: 2,
                      p: isMobile ? 2 : 3,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Quicksand-Regular",
                        fontSize: { xs: "14px", md: "16px" },
                        color: "#5D4E37",
                        lineHeight: 1.5,
                        "& strong": {
                          fontFamily: "Quicksand-Bold",
                          color: "#3E2F1F",
                        },
                      }}
                    >
                      {generarMensajeRechazo(datosApiCancelar, "cancelar")}
                    </Typography>
                  </Box>
                )}

                {/* Información si está permitido */}
                {datosApiCancelar.accion_permitida && (
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      color: "#626262",
                      lineHeight: 1.6,
                      mb: 2,
                      "& strong": {
                        fontFamily: "Quicksand-Bold",
                      },
                    }}
                  >
                    Tienes hasta el <strong>{formatearFechaLimite(datosApiCancelar.fecha_limite_cancelar)}</strong> para suspender tu sesión.
                  </Typography>
                )}

                {/* Botón CONTINUAR SUSPENDER */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    onClick={() => handleSeleccionAccion("cancelar")}
                    disabled={!datosApiCancelar.accion_permitida}
                    disableRipple
                    disableElevation
                    disableFocusRipple
                    sx={{
                      width: { xs: "380px", md: "420px" },
                      backgroundColor: datosApiCancelar.accion_permitida ? "#3B82F6" : "#d0d0d0",
                      color: "#FFFFFF",
                      padding: "14px 40px",
                      borderRadius: 2,
                      fontFamily: "Quicksand-Bold",
                      fontSize: { xs: "14px", md: "16px" },
                      textTransform: "none",
                      cursor: datosApiCancelar.accion_permitida ? "pointer" : "not-allowed",
                      boxShadow: "none !important",
                      transition: "none !important",
                      "&:hover": {
                        backgroundColor: datosApiCancelar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:active": {
                        backgroundColor: datosApiCancelar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:focus": {
                        backgroundColor: datosApiCancelar.accion_permitida ? "#2563EB" : "#d0d0d0",
                        boxShadow: "none !important",
                      },
                      "&:disabled": {
                        backgroundColor: "#d0d0d0",
                        color: "#FFFFFF",
                      },
                      "& .MuiTouchRipple-root": {
                        display: "none",
                      },
                    }}
                  >
                    CONTINUAR SUSPENDER
                  </Button>
                </Box>
              </Box>

            </Box>
          </Box>
        </Box>
      </>
    );
  }

  // Renderizado principal (cuando ya seleccionó una acción o es una acción directa)
  const accionEfectiva = accionTipo === "reagendarocancelar" ? accionSeleccionada : accionTipo;

  return (
    <>
      {/* Logo Clinifica */}
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />

      {/* Caja superior con información de sesión */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          mb: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fbfafe",
            borderRadius: 2,
            p: isMobile ? 2 : 4,
            boxShadow: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#626262",
              mb: 0,
            }}
          >
            Estás {accionEfectiva === "confirmar" ? "confirmando" : accionEfectiva === "reagendar" ? "reagendando" : "suspendiendo"} tu sesión con:
          </Typography>

          <Typography
            sx={{
              mt: -0.5,
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "18px", md: "20px" },
              color: "#191C1F",
              mb: 0,
            }}
          >
            {datosApi.profesional_nombres} {datosApi.profesional_apellidos}
          </Typography>

          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              mt: -0.5,
              mb: 0,
            }}
          >
            {datosApi.profesional_profesion}
          </Typography>

          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#626262",
              mt: 2,
              mb: 0,
            }}
          >
            El servicio de:
          </Typography>

          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "18px", md: "20px" },
              color: "#191C1F",
              mt: -0.5,
              mb: 0,
            }}
          >
            {datosApi.sesion_nombre_servicio}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mt: -0.5, mb: 0, flexWrap: "wrap" }}>
            <Typography
              sx={{
                fontFamily: "Quicksand-Regular",
                fontSize: { xs: "14px", md: "16px" },
                mr: 0.5,
              }}
            >
              ${datosApi.valor_cobro.toLocaleString("es-CL")}
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "16px" },
                color: "#007FFF",
              }}
            >
              {datosApi.estado_cobro === "pagado" ? "pagado" : "pendiente de pago"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#f0f4ff",
              color: "#3B82F6",
              fontFamily: "Quicksand-Medium",
              fontSize: { xs: "13px", md: "15px" },
              borderRadius: 2,
              paddingLeft: 0.75,
              paddingRight: 0.75,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              marginLeft: -0.75,
              mt: -0.75,
              textAlign: "left",
            }}
          >
            {transformModalidad(datosApi.sesion_tipo)}
          </Box>

          <Typography
            sx={{
              mt: 0,
              color: "#007FFF",
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            {new Date(datosApi.sesion_fecha + "T00:00:00").toLocaleDateString("es-ES", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })} - {datosApi.sesion_hora}
          </Typography>
        </Box>
      </Box>

      {/* Título de sección */}
      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { xs: "20px", md: "24px" },
          textAlign: "center",
          color: "#191C1F",
          mt: 3,
          mb: 1,
        }}
      >
        {accionEfectiva === "confirmar" && "Confirmar asistencia"}
        {accionEfectiva === "reagendar" && "Reagendar sesión"}
        {accionEfectiva === "cancelar" && "Suspender sesión"}
      </Typography>

      {/* Caja inferior con acción */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          mb: 5,
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: isMobile ? 2 : 4,
            boxShadow: 1,
          }}
        >
          {/* Wrapper con padding lateral para desktop */}
          <Box sx={{ px: { xs: 0, md: "15%" } }}>
            {/* Mensaje de alerta si accion_permitida es false */}
            {!datosApi.accion_permitida && (
              <Box
                sx={{
                  backgroundColor: "#FEF3E2",
                  borderRadius: 2,
                  p: isMobile ? 2 : 3,
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { xs: "14px", md: "16px" },
                    color: "#5D4E37",
                    lineHeight: 1.5,
                    "& strong": {
                      fontFamily: "Quicksand-Bold",
                      color: "#3E2F1F",
                    },
                  }}
                >
                  {generarMensajeRechazo(datosApi, accionEfectiva)}
                </Typography>
              </Box>
            )}

            {/* Contenido según acción y si está permitida */}
            {datosApi.accion_permitida && accionEfectiva === "confirmar" && (
              <ConfirmarSeccion
                datosApi={datosApi}
                confirmando={confirmando}
                confirmadoExito={confirmadoExito}
                errorConfirmacion={errorConfirmacion}
                mostrarIcono={mostrarIcono}
                onConfirmar={handleConfirmar}
              />
            )}

            {datosApi.accion_permitida && accionEfectiva === "reagendar" && (
              <ReagendarSeccion
                datosApi={datosApi}
                reagendando={reagendando}
                reagendadoExito={reagendadoExito}
                errorReagendacion={errorReagendacion}
                mostrarIconoReagendar={mostrarIconoReagendar}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                horasDisponibles={horasDisponibles}
                onReagendar={handleReagendar}
                onSeleccionFecha={manejarSeleccionFecha}
                onSeleccionHora={manejarSeleccionHora}
                formatearFechaLimite={formatearFechaLimite}
              />
            )}

            {datosApi.accion_permitida && accionEfectiva === "cancelar" && (
              <CancelarSeccion
                datosApi={datosApi}
                suspendiendo={suspendiendo}
                suspendidoExito={suspendidoExito}
                errorSuspension={errorSuspension}
                mostrarIconoSuspender={mostrarIconoSuspender}
                onCancelar={handleCancelar}
                formatearFechaLimite={formatearFechaLimite}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default GestionSesion;

